(() => {
  if (window.drboSeriesCommerce) return;
  window.drboSeriesCommerce = true;
  const english = /^en(?:-|$)/i.test(document.documentElement.lang);
  const copy = (german, translated) => english ? translated : german;
  const offers = Array.from(document.querySelectorAll('[data-s5-offer]'));
  const variants = new Map(offers.map(offer => [offer, JSON.parse(offer.querySelector('[data-s5-variants]').textContent)]));
  let pending = false;
  const uncertainHandles = new Set();

  async function cartRequest(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const body = await response.json();
      return { response, body };
    } finally {
      clearTimeout(timeout);
    }
  }

  const selectedVariant = offer => variants.get(offer).find(variant => String(variant.id) === offer.querySelector('[name="id"]').value);
  const permitted = (offer, variant) => Boolean(variant?.available && offer.dataset.restricted !== 'true' && offer.dataset.requiresPlan !== 'true' && !uncertainHandles.has(offer.dataset.handle));

  function updateOffer(offer) {
    const variant = selectedVariant(offer);
    const allowed = permitted(offer, variant);
    offer.dataset.unavailable = String(!allowed);
    offer.querySelectorAll('[data-s5-form] button').forEach(button => { button.disabled = pending || !allowed; });
    if (!variant) return;
    offer.querySelector('[data-s5-price]').textContent = variant.price;
    const compare = offer.querySelector('[data-s5-compare]');
    const saving = offer.querySelector('[data-s5-saving]');
    compare.hidden = saving.hidden = !variant.hasSaving;
    compare.textContent = variant.compare;
    saving.textContent = `${copy('Du sparst', 'Save')} ${variant.saving}`;
    offer.querySelector('[name="quantity"]').value = variant.quantity;
    const stock = offer.querySelector('[data-s5-stock]');
    stock.textContent = offer.dataset.restricted === 'true' ? copy('Nicht nach Österreich lieferbar', 'Not available in Austria') : !variant.available ? copy('Aktuell nicht verfügbar', 'Currently unavailable') : variant.inStock ? copy('Auf Lager', 'In stock') : copy('Verfügbar', 'Available');
    const details = offer.querySelector('[data-s5-details]');
    const url = new URL(offer.querySelector('[data-s5-form]').dataset.productUrl, location.origin);
    url.searchParams.set('variant', variant.id);
    details.href = url.href;
  }

  offers.forEach(offer => {
    updateOffer(offer);
    offer.querySelector('[data-s5-variant]')?.addEventListener('change', event => {
      offers.filter(other => other.dataset.handle === offer.dataset.handle).forEach(other => {
        other.querySelector('[name="id"]').value = event.target.value;
        updateOffer(other);
      });
    });
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-s5-form]');
    if (!form) return;
    event.preventDefault();
    if (pending) return;
    const offer = form.closest('[data-s5-offer]');
    const variant = selectedVariant(offer);
    if (!permitted(offer, variant) || !form.reportValidity()) return;
    const checkout = event.submitter?.dataset.intent === 'checkout';
    const status = offer.querySelector('[data-s5-status]');
    const cartLink = offer.querySelector('[data-s5-cart-link]');
    const cartUrl = form.dataset.cartUrl;
    let confirmed = false;
    let navigating = false;
    let requestSent = false;
    let beforeCart;
    pending = true;
    form.setAttribute('aria-busy', 'true');
    offers.forEach(updateOffer);
    status.hidden = false;
    status.removeAttribute('data-error');
    status.textContent = copy('Wird zum Warenkorb hinzugefügt …', 'Adding to cart …');
    cartLink.hidden = true;
    try {
      const { response: baselineResponse, body: baseline } = await cartRequest(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!baselineResponse.ok) throw new Error('cart-unavailable');
      beforeCart = baseline;
      requestSent = true;
      const { response, body: result } = await cartRequest(`${form.action}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: variant.quantity }] })
      });
      if (!response.ok) {
        const failure = new Error('cart-rejected');
        failure.customerMessage = typeof result.description === 'string' ? result.description : null;
        throw failure;
      }
      confirmed = true;
      status.textContent = `${offer.dataset.label} ${copy('wurde zum Warenkorb hinzugefügt.', 'has been added to your cart.')}`;
      cartLink.hidden = false;
      if (checkout) {
        const checkoutForm = document.createElement('form');
        checkoutForm.method = 'post';
        checkoutForm.action = cartUrl;
        const checkoutField = document.createElement('input');
        checkoutField.type = 'hidden';
        checkoutField.name = 'checkout';
        checkoutField.value = '1';
        checkoutForm.append(checkoutField);
        document.body.append(checkoutForm);
        checkoutForm.submit();
        navigating = true;
        return;
      }
      try {
        const { response: cartResponse, body: cart } = await cartRequest(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) {
          if (window.themeCore) window.themeCore.cartObject = cart;
          window.themeCore?.EventBus?.emit('header:update-item-count', { item_count: cart.item_count });
        }
        window.themeCore?.EventBus?.emit('cart:refresh');
      } catch (refreshError) {
        cartLink.hidden = false;
      }
    } catch (error) {
      let unchanged = !requestSent;
      if (requestSent && beforeCart) {
        try {
          const { response: afterResponse, body: afterCart } = await cartRequest(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
          if (afterResponse.ok) {
            const count = cart => cart.items.filter(item => item.variant_id === variant.id).reduce((total, item) => total + item.quantity, 0);
            unchanged = error.message === 'cart-rejected' && count(beforeCart) === count(afterCart);
          }
        } catch (verifyError) { unchanged = false; }
      }
      if (!unchanged) uncertainHandles.add(offer.dataset.handle);
      status.setAttribute('data-error', '');
      status.textContent = confirmed ? copy('Der Artikel ist im Warenkorb. Öffne den Warenkorb, um zur Kasse zu gehen.', 'The item is in your cart. Open your cart to proceed to checkout.') : !unchanged ? copy('Bitte prüfe Deinen Warenkorb. Um doppelte Artikel zu vermeiden, wurde dieser Kaufbutton vorübergehend gesperrt.', 'Please check your cart. This purchase button has been paused to avoid adding duplicate items.') : error.customerMessage || copy('Das Hinzufügen ist fehlgeschlagen. Bitte versuche es erneut.', 'The item could not be added. Please try again.');
      cartLink.hidden = false;
    } finally {
      if (!navigating) {
        pending = false;
        form.removeAttribute('aria-busy');
        offers.forEach(updateOffer);
      }
    }
  });
  addEventListener('pageshow', () => {
    pending = false;
    offers.forEach(offer => { offer.querySelector('form').removeAttribute('aria-busy'); updateOffer(offer); });
  });
})();
